import type { EliteOnboardingStatusResponse } from '../../../../types/misc.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import { isYearlyPlanTenure } from '../../eliteOnboardingPlanTenure'

/** Verb for the primary-not-Elite family offer headline (“Get” vs “Renew”). Only used by this card. */
export enum EliteFamilyOfferCardIntent {
  Get = 'GET',
  Renew = 'RENEW'
}

type EligiblePlan = EliteOnboardingStatusResponse['eligiblePlans'][number]

export type NewEliteFamilyPrimaryNonEliteOfferCardProps = {
  familyMemberDisplayName: string
  eligiblePlans: EliteOnboardingStatusResponse['eligiblePlans']
  eliteIntent?: EliteFamilyOfferCardIntent
}

function resolveOfferPlan(plans: EliteOnboardingStatusResponse['eligiblePlans']): EligiblePlan | undefined {
  const yearly = plans.find((plan) => isYearlyPlanTenure(plan?.effectivePlanTenure ?? plan?.basePlanTenure))
  if (yearly) return yearly
  return plans[0]
}

export default function NewEliteFamilyPrimaryNonEliteOfferCard({
  familyMemberDisplayName,
  eligiblePlans,
  eliteIntent = EliteFamilyOfferCardIntent.Get
}: NewEliteFamilyPrimaryNonEliteOfferCardProps) {
  const plan = resolveOfferPlan(eligiblePlans ?? [])
  const isYearly = plan ? isYearlyPlanTenure(plan?.effectivePlanTenure ?? plan?.basePlanTenure) : true
  const planWord = isYearly ? 'Yearly' : 'Monthly'
  const tenureSuffix = isYearly ? '/yr' : '/mo'

  const discountedAmount = plan?.effectivePlanFees?.amount ?? plan?.basePlanFees?.amount ?? 0
  const baseAmount = plan?.basePlanFees?.amount ?? 0
  const showStrikethrough = baseAmount > discountedAmount

  const displayName = familyMemberDisplayName.trim() ? familyMemberDisplayName.trim() : 'your family member'

  let headlineVerb = 'Get'
  if (eliteIntent === EliteFamilyOfferCardIntent.Renew) {
    headlineVerb = 'Renew'
  }

  return (
    <div className='mx-5 rounded-xl bg-white px-6 pb-5 pt-4'>
      <div className='flex flex-col items-center gap-[0.8rem]'>
        <div className='flex w-full max-w-[327px] flex-col items-center gap-2'>
          <p className='text-center text-[14px] font-medium leading-[1.25] text-[#4D3E38]'>
            <span>
              {headlineVerb} Elite {planWord} for{' '}
            </span>
            <span className='inline-block max-w-[min(160px,45vw)] truncate align-bottom text-[#4D3E38]'>
              {displayName}
            </span>
            <span> at just</span>
          </p>
          <div className='flex flex-wrap items-center justify-center gap-1'>
            <div className='flex items-baseline gap-0.5 text-[#CC5C2D]'>
              <span className='text-[21px] font-medium leading-[1.25]'>
                {discountedAmount > 0
                  ? `${roundOffAndFormatNumberWithCurrency(discountedAmount)}${tenureSuffix}`
                  : '—'}
              </span>
              <span className='text-[12px] font-normal leading-[1.25]'>+GST</span>
            </div>
            {showStrikethrough ? (
              <span className='text-[17px] font-normal leading-[1.5] text-[#8E9393] line-through opacity-50'>
                {roundOffAndFormatNumberWithCurrency(baseAmount)}
                {tenureSuffix}
              </span>
            ) : null}
          </div>
        </div>
        <p className='w-full text-center text-[13px] font-normal leading-[1.6] text-[#6A6E6E]'>
          You need to be an Elite member to avail the special price for family members
        </p>
      </div>
    </div>
  )
}
